import { useMemo } from 'react';
import { Permissions, usePermission } from '@billda/permissions-ui';
import type { PotDetailsV2Response } from '@billda/api';
import {
  LedgerHeader,
  Money,
  Text,
  cn,
  ledgerContainerClass,
} from '@billda/design-system';
import {
  POT_LEDGER_COLLAPSE,
  POT_LEDGER_COLUMNS,
  POT_LEDGER_CONTAINER,
} from './columns';
import { PotLedgerRow, type PotLedgerRowActions } from './PotLedgerRow';
import { toPotLedgerRows, type PotLedgerEntry } from './toPotLedgerRows';

/**
 * Closing total for the rows on screen. Deductions carry negative amounts, so
 * this is what the breakdown nets to, and it follows the search filter rather
 * than describing the whole pot.
 *
 * Cancelled work is excluded, matching what the breakdown summed before the
 * ledger. G5: the design argues rejected work should drop out too, and that the
 * backend should own sums like this. Both are still open, so the arithmetic
 * here is deliberately the old arithmetic. The Overview tab stays the authority
 * on the pot's gross, costs and net.
 */
function PotLedgerTotal({ rows }: { rows: readonly PotLedgerEntry[] }) {
  const counted = rows.filter(entry => entry.status !== 'Cancelled');
  const known = counted.filter(entry => entry.amount != null);
  const total =
    counted.length > 0 && known.length === 0
      ? null
      : known.reduce((sum, entry) => sum + (entry.amount ?? 0), 0);

  return (
    <div
      data-testid="pot-ledger-total"
      className="flex items-center gap-3 border-t-2 border-border-default px-4 py-2"
    >
      <div className="min-w-0 flex-1">
        <Text variant="body2" className="font-semibold">
          Total
        </Text>
      </div>
      <div className="w-(--ledger-value-width) shrink-0 text-right">
        <Money value={total} />
      </div>
    </div>
  );
}

export const POT_LEDGER_TESTID = 'pot-breakdown-ledger';

export interface PotBreakdownLedgerProps {
  pot: PotDetailsV2Response;
  search?: string;
  actions: PotLedgerRowActions;
}

/**
 * The pot breakdown as a flat ledger, ordered by site, plot and booked time,
 * with a Site / Plot column instead of bands. A pot is one gang for one week,
 * so there is no second dimension left worth grouping by.
 *
 * There are no per-group totals, only a closing total for the rows on screen.
 * The pot's gross, costs and net live on the Overview tab, computed by the
 * backend.
 *
 * Money always renders here — the pot screen has no show-values toggle. A
 * viewer without financial permission gets nulled amounts from the API, and
 * `Money` renders those as withheld rather than as zero.
 */
export function PotBreakdownLedger({
  pot,
  search,
  actions,
}: PotBreakdownLedgerProps) {
  const canCancelAllocations = usePermission(
    Permissions.Allocations.Admin.Cancel
  );
  const rows = useMemo(
    () =>
      toPotLedgerRows({
        allocations: pot.allocations,
        deductions: pot.deductions,
        search,
      }),
    [pot.allocations, pot.deductions, search]
  );

  if (rows.length === 0) {
    return (
      <div className="p-6 text-center" data-testid={POT_LEDGER_TESTID}>
        <Text variant="body1" color="secondary">
          {search ? 'No results match your search.' : 'No breakdown data.'}
        </Text>
      </div>
    );
  }

  return (
    <div
      data-testid={POT_LEDGER_TESTID}
      className={cn(
        'flex-1 min-h-0 overflow-auto',
        ledgerContainerClass(POT_LEDGER_CONTAINER)
      )}
    >
      <LedgerHeader
        columns={POT_LEDGER_COLUMNS}
        collapse={POT_LEDGER_COLLAPSE}
      />
      {rows.map(entry => (
        <PotLedgerRow
          key={entry.key}
          entry={entry}
          potStatus={pot.status}
          canCancelAllocations={canCancelAllocations}
          actions={actions}
        />
      ))}
      <PotLedgerTotal rows={rows} />
    </div>
  );
}
